import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateGRN } from '@/hooks/finance/useGRN';
import { CreateGRNItemInput } from '@/types/database/grn';
import { toast } from 'sonner';

interface GRNLineItem extends CreateGRNItemInput {
  id: string;
}

const NewGRNPage = () => {
  const navigate = useNavigate();
  const createGRN = useCreateGRN();

  const [formData, setFormData] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    delivery_note_number: '',
    vehicle_number: '',
    driver_name: '',
    warehouse_location: '',
    notes: '',
  });

  const [items, setItems] = useState<GRNLineItem[]>([
    { id: '1', product_name: '', unit: 'kg', ordered_quantity: 0, received_quantity: 0 },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (id: string, field: keyof GRNLineItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, { 
      id: Date.now().toString(), 
      product_name: '', 
      unit: 'kg', 
      ordered_quantity: 0, 
      received_quantity: 0 
    }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleSubmit = async () => {
    if (!formData.delivery_date) {
      toast.error('Please enter delivery date');
      return;
    }

    if (items.every(i => !i.product_name)) {
      toast.error('Please add at least one item');
      return;
    }

    setIsSubmitting(true);
    try {
      await createGRN.mutateAsync({
        ...formData,
        items: items.filter(i => i.product_name).map(({ id, ...item }) => item),
      });
      navigate('/finance/accounting/grn');
    } catch (error) {
      console.error('Failed to create GRN:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">New Goods Received Note</h1>
            <p className="text-muted-foreground">Record incoming goods delivery</p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Delivery Information */}
          <Card>
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
              <CardDescription>Details about the delivery</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="delivery_date">Delivery Date *</Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_note_number">Delivery Note Number</Label>
                  <Input
                    id="delivery_note_number"
                    placeholder="DN-12345"
                    value={formData.delivery_note_number}
                    onChange={(e) => handleInputChange('delivery_note_number', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_number">Vehicle Number</Label>
                  <Input
                    id="vehicle_number"
                    placeholder="Plate number"
                    value={formData.vehicle_number}
                    onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver_name">Driver Name</Label>
                  <Input
                    id="driver_name"
                    placeholder="Driver's name"
                    value={formData.driver_name}
                    onChange={(e) => handleInputChange('driver_name', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="warehouse_location">Warehouse Location</Label>
                <Input
                  id="warehouse_location"
                  placeholder="Where goods are being stored"
                  value={formData.warehouse_location}
                  onChange={(e) => handleInputChange('warehouse_location', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle>Items Received</CardTitle>
              <CardDescription>List of goods received</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-start p-4 border rounded-lg">
                    <div className="flex-1 space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Product Name *</Label>
                          <Input
                            placeholder="Product name"
                            value={item.product_name}
                            onChange={(e) => handleItemChange(item.id, 'product_name', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit</Label>
                          <Select
                            value={item.unit}
                            onValueChange={(v) => handleItemChange(item.id, 'unit', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kg">Kilograms (kg)</SelectItem>
                              <SelectItem value="units">Units</SelectItem>
                              <SelectItem value="boxes">Boxes</SelectItem>
                              <SelectItem value="crates">Crates</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Ordered Quantity</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.ordered_quantity || ''}
                            onChange={(e) => handleItemChange(item.id, 'ordered_quantity', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Received Quantity *</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.received_quantity || ''}
                            onChange={(e) => handleItemChange(item.id, 'received_quantity', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Quality Notes</Label>
                        <Input
                          placeholder="Any quality observations"
                          value={item.quality_notes || ''}
                          onChange={(e) => handleItemChange(item.id, 'quality_notes', e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Any additional notes about this delivery..."
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              Create GRN
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewGRNPage;
